// frontend_MainDashboard/src/hooks/api/useApiBase.ts

import { useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import { API_BASE_URL } from '../../constants';

// Custom Error class for API errors
export class ApiError extends Error {
  status?: number;
  data?: any;

  constructor(message: string, status?: number, data?: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Centralized token refresh to avoid multiple parallel refreshes ("refresh stampede").
 * Any request that hits 401 will await the same in-flight refresh promise.
 */
let refreshPromise: Promise<string> | null = null;
const refreshIdToken = async (user: { getIdToken: (forceRefresh?: boolean) => Promise<string> } | null): Promise<string> => {
  if (!user) {
    throw new ApiError('Not authenticated', 401);
  }
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = user.getIdToken(true)
    .then((t) => {
      refreshPromise = null;
      return t;
    })
    .catch((e) => {
      refreshPromise = null;
      throw e;
    });
  return refreshPromise;
};

export interface ApiBaseHook {
  makeRequest: <T_Response = any, T_RequestBody = any>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body?: T_RequestBody,
    options?: { signal?: AbortSignal }
  ) => Promise<T_Response>;
  
  makeFormDataRequest: <T_Response = any>(
    endpoint: string,
    method: 'POST' | 'PUT',
    formData: FormData,
    onProgress?: (progress: number) => void
  ) => Promise<T_Response>;
  
  makeRequestWithRetry: <T_Response = any, T_RequestBody = any>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body?: T_RequestBody,
    maxRetries?: number,
    retryDelay?: number
  ) => Promise<T_Response>;
}

export const useApiBase = (): ApiBaseHook => {
  const { idToken, currentUser } = useAuth();
  const { selectedClientId } = useClient();

  const makeRequest = useCallback(async <T_Response = any, T_RequestBody = any>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body?: T_RequestBody,
    options?: { signal?: AbortSignal }
  ): Promise<T_Response> => {
    if (!idToken) {
      console.error("useApiBase: No ID token available for API request.");
      throw new ApiError('Authentication token is missing. Please log in.', 401);
    }

    const normalizedBaseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${normalizedBaseUrl}${normalizedEndpoint}`;

    const headers: HeadersInit = {
      'Authorization': `Bearer ${idToken}`,
    };

    // Inject X-Client-ID header for multi-user support
    if (selectedClientId) {
      headers['X-Client-ID'] = selectedClientId;
    }

    const config: RequestInit = {
      method,
      headers,
      signal: options?.signal,
    };

    if (body && (method === 'POST' || method === 'PUT')) {
      headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify(body);
    }

    let response;
    try {
        response = await fetch(url, config);
    } catch (networkError: any) {
        if (networkError?.name === 'AbortError') {
          throw new ApiError('Request aborted', 499, { type: 'AbortError' });
        }
        console.error(`API Network Error (${method} ${url}):`, networkError);
        throw new ApiError(networkError?.message || 'A network error occurred. Please check your connection.', 0, { type: 'NetworkError' });
    }

    let responseData: any = null;
    try {
        if (response.status === 204) { // No Content
            // responseData remains null
        } else {
            const text = await response.text();
            if (text) {
                responseData = JSON.parse(text);
            }
        }
    } catch (e) {
        if (response.ok) {
            console.error(`API Error (${method} ${url}): Successfully received but failed to parse JSON response. Status: ${response.status}`, await response.text().catch(() => ""));
            throw new ApiError(`Invalid JSON response from server. Status: ${response.status}`, response.status, { rawResponseText: await response.text().catch(() => "") });
        }
        throw new ApiError(response.statusText || `Invalid response from server. Status: ${response.status}`, response.status, { rawResponseText: await response.text().catch(()=>"") });
    }

    if (!response.ok) {
      const errorMessage = responseData?.message || responseData?.error || `API request failed with status ${response.status}`;
      
      // On 401, serialize a token refresh and retry once with the fresh token
      if (response.status === 401 && currentUser) {
        console.log("useApiBase: 401 detected, serializing token refresh and retry");
        try {
          if (options?.signal?.aborted) {
            throw new ApiError('Request aborted', 499, { type: 'AbortError' });
          }
          const freshToken = await refreshIdToken(currentUser);

          const retryHeaders: HeadersInit = {
            'Authorization': `Bearer ${freshToken}`,
          };
          if (selectedClientId) {
            retryHeaders['X-Client-ID'] = selectedClientId;
          }

          const retryConfig: RequestInit = {
            method,
            headers: retryHeaders,
            signal: options?.signal,
          };
          if (body && (method === 'POST' || method === 'PUT')) {
            retryHeaders['Content-Type'] = 'application/json';
            retryConfig.body = JSON.stringify(body);
          }

          const retryResponse = await fetch(url, retryConfig);

          let retryResponseData: any = null;
          if (retryResponse.status !== 204) {
            const retryText = await retryResponse.text();
            if (retryText) {
              retryResponseData = JSON.parse(retryText);
            }
          }

          if (retryResponse.ok) {
            console.log("useApiBase: Retry successful after token refresh");
            return retryResponseData as T_Response;
          } else {
            const retryErrorMessage = retryResponseData?.message || retryResponseData?.error || `API request failed with status ${retryResponse.status}`;
            console.error(`API Error (${method} ${url}) after retry: ${retryResponse.status} - ${retryErrorMessage}`, retryResponseData);
            throw new ApiError(retryErrorMessage, retryResponse.status, retryResponseData);
          }
        } catch (refreshErr: any) {
          console.error("useApiBase: Token refresh failed:", refreshErr);
          // Fall through to original error
        }
      }

      console.error(`API Error (${method} ${url}): ${response.status} - ${errorMessage}`, responseData);
      throw new ApiError(errorMessage, response.status, responseData);
    }

    return responseData as T_Response;
  }, [idToken, currentUser, selectedClientId]);

  const makeFormDataRequest = useCallback(async <T_Response = any>(
    endpoint: string,
    method: 'POST' | 'PUT',
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<T_Response> => {
    if (!idToken) {
      console.error("useApiBase (makeFormDataRequest): No ID token available.");
      throw new ApiError('Authentication token is missing. Please log in.', 401);
    }

    const normalizedBaseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${normalizedBaseUrl}${normalizedEndpoint}`;

    const headers: HeadersInit = {
      'Authorization': `Bearer ${idToken}`,
    };

    // Inject X-Client-ID header for multi-user support
    if (selectedClientId) {
      headers['X-Client-ID'] = selectedClientId;
    }

    // If caller provided onProgress, use XMLHttpRequest to support progress events
    if (onProgress) {
      return await new Promise<T_Response>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url);
        xhr.setRequestHeader('Authorization', `Bearer ${idToken}`);
        if (selectedClientId) {
          xhr.setRequestHeader('X-Client-ID', selectedClientId);
        }

        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable) {
            const pct = Math.round((ev.loaded / ev.total) * 100);
            try { onProgress(pct); } catch {}
          }
        };

        xhr.onreadystatechange = async () => {
          if (xhr.readyState !== 4) return;
          const status = xhr.status;
          let responseText = '';
          try { responseText = xhr.responseText || ''; } catch {}
          let parsed: any = null;
          try { if (responseText) parsed = JSON.parse(responseText); } catch (e) {
            if (status >= 200 && status < 300) {
              console.error(`API FormData Error (${method} ${url}): Successfully received but failed to parse JSON response. Status: ${status}`, responseText);
              reject(new ApiError(`Invalid JSON response from server after file upload. Status: ${status}`, status, { rawResponseText: responseText }));
              return;
            }
            reject(new ApiError(xhr.statusText || `Invalid response from server after file upload. Status: ${status}`, status, { rawResponseText: responseText }));
            return;
          }

          if (status === 401 && currentUser) {
            try {
              const fresh = await refreshIdToken(currentUser);
              const xhr2 = new XMLHttpRequest();
              xhr2.open(method, url);
              xhr2.setRequestHeader('Authorization', `Bearer ${fresh}`);
              if (selectedClientId) {
                xhr2.setRequestHeader('X-Client-ID', selectedClientId);
              }
              xhr2.upload.onprogress = xhr.upload.onprogress;
              xhr2.onreadystatechange = () => {
                if (xhr2.readyState !== 4) return;
                const s2 = xhr2.status;
                let resp2 = xhr2.responseText || '';
                try {
                  const parsed2 = resp2 ? JSON.parse(resp2) : null;
                  if (s2 >= 200 && s2 < 300) {
                    resolve(parsed2 as T_Response);
                  } else {
                    const msg = parsed2?.message || parsed2?.error || `API request failed with status ${s2}`;
                    reject(new ApiError(msg, s2, parsed2));
                  }
                } catch (e) {
                  reject(new ApiError(xhr2.statusText || `Invalid response after retry. Status: ${s2}`, s2, { rawResponseText: resp2 }));
                }
              };
              xhr2.send(formData);
              return;
            } catch (refreshErr) {
              console.error('useApiBase (FormData): Token refresh failed:', refreshErr);
            }
          }

          if (status >= 200 && status < 300) {
            resolve(parsed as T_Response);
          } else {
            const errorMessage = parsed?.message || parsed?.error || `API request failed with status ${status}`;
            reject(new ApiError(errorMessage, status, parsed));
          }
        };

        xhr.onerror = () => {
          reject(new ApiError('A network error occurred during file upload. Please check your connection.', 0, { type: 'NetworkError' }));
        };

        xhr.send(formData);
      });
    }

    // Fallback to fetch when no progress callback requested
    const config: RequestInit = {
      method,
      headers,
      body: formData,
    };

    let response;
    try {
        response = await fetch(url, config);
    } catch (networkError: any) {
        console.error(`API FormData Network Error (${method} ${url}):`, networkError);
        throw new ApiError(networkError.message || 'A network error occurred during file upload. Please check your connection.', 0, { type: 'NetworkError' });
    }

    let responseData: any = null;
    try {
        const text = await response.text();
        if (text) {
            responseData = JSON.parse(text);
        }
    } catch (e) {
        if (response.ok) {
            console.error(`API FormData Error (${method} ${url}): Successfully received but failed to parse JSON response. Status: ${response.status}`, await response.text().catch(() => ""));
            throw new ApiError(`Invalid JSON response from server after file upload. Status: ${response.status}`, response.status, { rawResponseText: await response.text().catch(() => "") });
        }
         throw new ApiError(response.statusText || `Invalid response from server after file upload. Status: ${response.status}`, response.status, { rawResponseText: await response.text().catch(()=>"") });
    }

    if (!response.ok) {
      const errorMessage = responseData?.message || responseData?.error || `API request failed with status ${response.status}`;
      
      if (response.status === 401 && currentUser) {
        console.log("useApiBase (FormData): 401 detected, serializing token refresh and retry");
        try {
          const freshToken = await refreshIdToken(currentUser);

          const retryHeaders: HeadersInit = {
            'Authorization': `Bearer ${freshToken}`,
          };
          if (selectedClientId) {
            retryHeaders['X-Client-ID'] = selectedClientId;
          }

          const retryConfig: RequestInit = {
            method,
            headers: retryHeaders,
            body: formData,
          };

          const retryResponse = await fetch(url, retryConfig);

          let retryResponseData: any = null;
          const retryText = await retryResponse.text();
          if (retryText) {
            retryResponseData = JSON.parse(retryText);
          }

          if (retryResponse.ok) {
            console.log("useApiBase (FormData): Retry successful after token refresh");
            return retryResponseData as T_Response;
          } else {
            const retryErrorMessage = retryResponseData?.message || retryResponseData?.error || `API request failed with status ${retryResponse.status}`;
            console.error(`API FormData Error (${method} ${url}) after retry: ${retryResponse.status} - ${retryErrorMessage}`, retryResponseData);
            throw new ApiError(retryErrorMessage, retryResponse.status, retryResponseData);
          }
        } catch (refreshErr: any) {
          console.error("useApiBase (FormData): Token refresh failed:", refreshErr);
        }
      }

      console.error(`API FormData Error (${method} ${url}): ${response.status} - ${errorMessage}`, responseData);
      throw new ApiError(errorMessage, response.status, responseData);
    }

    return responseData as T_Response;
  }, [idToken, currentUser, selectedClientId]);

  const makeRequestWithRetry = useCallback(async <T_Response = any, T_RequestBody = any>(
    endpoint: string,
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body?: T_RequestBody,
    maxRetries: number = 3,
    retryDelay: number = 1000
  ): Promise<T_Response> => {
    let lastError: ApiError;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await makeRequest<T_Response, T_RequestBody>(endpoint, method, body);
      } catch (error) {
        lastError = error as ApiError;
        
        // Don't retry on client errors (4xx) except for 401 (handled by makeRequest), 408 (timeout) and 429 (rate limit)
        if (lastError.status && lastError.status >= 400 && lastError.status < 500 && 
            lastError.status !== 401 && lastError.status !== 408 && lastError.status !== 429) {
          throw lastError;
        }
        
        // Don't retry on the last attempt
        if (attempt === maxRetries) {
          throw lastError;
        }
        
        // Log retry attempt for debugging
        console.warn(`API request failed (attempt ${attempt + 1}/${maxRetries + 1}): ${lastError.message}`);
        
        // Exponential backoff with jitter
        const delay = retryDelay * Math.pow(2, attempt) + Math.random() * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError!;
  }, [makeRequest]);

  return {
    makeRequest,
    makeFormDataRequest,
    makeRequestWithRetry,
  };
};
